import { Activity, type ComponentPropsWithRef, useState } from "react";
import Button from "@/components/ui/button.tsx";
import Icon from "@/components/ui/icon.tsx";
import Image from "@/components/ui/image.tsx";
import { useTranslations } from "@/features/translations/components/translations";
import { useAppSelector } from "@/redux/store.ts";
import type { Enum, ImageType } from "@/types";
import { cn } from "@/utils/style.ts";

const Preview = {
    Model: "model",
    TechnicalDrawing: "technical-drawing",
} as const;
type PreviewEnum = Enum<typeof Preview>;

export default function TechnicalDrawing() {
    const t = useTranslations();

    const [preview, setPreview] = useState<PreviewEnum>(Preview.TechnicalDrawing);

    const model = useAppSelector((state) => state.subFamily.image);
    const technicalDrawing = useAppSelector((state) => state.subFamily.technicalDrawing);

    const togglePreview = () => {
        setPreview((current) => (current === Preview.Model ? Preview.TechnicalDrawing : Preview.Model));
    };

    if (!model || !technicalDrawing) return null;

    return (
        <>
            <Activity mode={preview === Preview.Model ? "visible" : "hidden"}>
                <TechnicalDrawingImage>
                    <Image
                        image={technicalDrawing}
                        alt={t("technical_drawing")}
                        className="model-renderer__technical-drawing-image"
                    />
                </TechnicalDrawingImage>
            </Activity>
            <Button variant="icon" className="model-renderer__toggle" onClick={togglePreview}>
                <Activity mode={preview === Preview.Model ? "visible" : "hidden"}>
                    <TechnicalDrawingToggle image={model} label={t("3d_model")} />
                </Activity>
                <Activity mode={preview === Preview.TechnicalDrawing ? "visible" : "hidden"}>
                    <TechnicalDrawingToggle image={technicalDrawing} label={t("technical_drawing")} />
                </Activity>
            </Button>
        </>
    );
}

function TechnicalDrawingImage({ className, ...props }: ComponentPropsWithRef<"div">) {
    return <div {...props} className={cn("model-renderer__technical-drawing", className)} />;
}

type TechnicalDrawingToggleProps = { image: ImageType; label: string };
function TechnicalDrawingToggle({ image, label }: TechnicalDrawingToggleProps) {
    return (
        <>
            <Image image={image} alt={label} />
            <span className="model-renderer__toggle-label">
                <Icon name="file" />
                <span>{label}</span>
            </span>
        </>
    );
}
